// Off-screen flatten: takes the (originalFile, drawings, crop, downscaleMaxPx)
// handoff payload and produces a single JPEG Blob that matches what the user
// sees in the preview modal. The server stores these bytes directly as
// ProjectImage.image — no render round-trip.
//
// `renderAnnotatedPair` emits a small thumbnail alongside that upload blob from
// the SAME finished canvas, so a host can repaint a tile without waiting on the
// network and without decoding a multi-megapixel JPEG into a ~120px box.
//
// Shares the Drawing → canvas path with the markup editor and preview
// (canvas-utils.renderAllDrawings), so "what I see is what I submit" is a
// compile-time invariant, not a best-effort.

import { format } from "date-fns";

import { renderAllDrawings } from "./canvas-utils";
import { applyRotationTransform, getRotatedSize, type Rotation } from "./rotation";
import type { Drawing } from "./types";
import type { CropRect } from "./crop-overlay";

export interface RenderAnnotatedBlobInput {
    file: File;
    drawings: Drawing[];
    crop?: CropRect;
    /**
     * Clockwise quarter-turns applied to the source image BEFORE crop and
     * drawings. Drawings and `crop` are already stored in this corrected space,
     * so only the image itself needs the transform. Defaults to 0.
     */
    rotation?: Rotation;
    /** Cap the longest edge of the output. Omit for full-resolution output. */
    downscaleMaxPx?: number;
    /** 0.0 – 1.0. Defaults to 0.9 — visually lossless for construction photos. */
    quality?: number;
    /** Output mime. Defaults to "image/jpeg" — photos are lossy to begin with. */
    mimeType?: string;
}

export async function renderAnnotatedBlob(
    input: RenderAnnotatedBlobInput,
): Promise<Blob> {
    const { quality = 0.9, mimeType = "image/jpeg" } = input;
    return canvasToBlob(await compositeToCanvas(input), mimeType, quality);
}

/** Longest edge of the thumbnail `renderAnnotatedPair` emits. Big enough for a
 *  retina grid tile or a preview strip, small enough to decode instantly. */
export const THUMBNAIL_MAX_PX = 512;

export interface AnnotatedRenderPair {
    /** Full-resolution flattened bytes — what gets uploaded. */
    annotatedBlob: Blob;
    /**
     * The same image capped at {@link THUMBNAIL_MAX_PX} on its longest edge, for
     * painting tiles with. Re-encoded from the finished canvas, so it costs one
     * canvas-to-canvas `drawImage` plus an encode — never a second decode or a
     * second composite. When the composite is already within the cap the
     * full-resolution blob is reused as-is rather than pointlessly re-encoded.
     */
    thumbnailBlob: Blob;
}

/**
 * Composite once, encode twice. The upload blob and the thumbnail come off the
 * same canvas, so they cannot diverge — the "what I see is what I submit"
 * invariant covers both outputs.
 */
export async function renderAnnotatedPair(
    input: RenderAnnotatedBlobInput,
): Promise<AnnotatedRenderPair> {
    const { quality = 0.9, mimeType = "image/jpeg" } = input;
    const canvas = await compositeToCanvas(input);
    const annotatedBlob = await canvasToBlob(canvas, mimeType, quality);

    const longest = Math.max(canvas.width, canvas.height);
    if (longest <= THUMBNAIL_MAX_PX) {
        return { annotatedBlob, thumbnailBlob: annotatedBlob };
    }

    const scale = THUMBNAIL_MAX_PX / longest;
    const thumb = document.createElement("canvas");
    thumb.width = Math.max(1, Math.round(canvas.width * scale));
    thumb.height = Math.max(1, Math.round(canvas.height * scale));
    const thumbCtx = thumb.getContext("2d");
    if (!thumbCtx) {
        throw new Error("renderAnnotatedPair: 2D context unavailable");
    }
    thumbCtx.imageSmoothingQuality = "high";
    thumbCtx.drawImage(canvas, 0, 0, thumb.width, thumb.height);
    // 0.8 rather than the upload's 0.9 — at this size the difference is
    // invisible and the bytes are what make the repaint feel free.
    const thumbnailBlob = await canvasToBlob(thumb, mimeType, 0.8);
    return { annotatedBlob, thumbnailBlob };
}

function canvasToBlob(
    canvas: HTMLCanvasElement,
    mimeType: string,
    quality: number,
): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (blob) resolve(blob);
                else reject(new Error("renderAnnotatedBlob: canvas.toBlob returned null"));
            },
            mimeType,
            quality,
        );
    });
}

async function compositeToCanvas(
    input: RenderAnnotatedBlobInput,
): Promise<HTMLCanvasElement> {
    const { file, drawings, crop, rotation = 0, downscaleMaxPx } = input;

    const img = await loadImage(file);

    // Everything downstream of rotation — the crop rect and every drawing —
    // is stored in rotation-corrected space, so that is the space we crop in.
    const corrected = getRotatedSize(img.naturalWidth, img.naturalHeight, rotation);

    const srcX = crop?.x ?? 0;
    const srcY = crop?.y ?? 0;
    const srcW = crop?.width ?? corrected.width;
    const srcH = crop?.height ?? corrected.height;

    // Optional downscale applied to the FINAL output (after crop). Keeps the
    // drawing render resolution high — we downscale the composited canvas as
    // the last step so line thickness and text antialiasing stay clean.
    let dstW = srcW;
    let dstH = srcH;
    if (downscaleMaxPx && Math.max(srcW, srcH) > downscaleMaxPx) {
        const scale = downscaleMaxPx / Math.max(srcW, srcH);
        dstW = Math.round(srcW * scale);
        dstH = Math.round(srcH * scale);
    }

    const canvas = document.createElement("canvas");
    canvas.width = dstW;
    canvas.height = dstH;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("renderAnnotatedBlob: 2D context unavailable");
    }

    // Drawings and the crop rect are in ROTATION-CORRECTED image space. Put the
    // context into that space once (crop offset + downscale), then paint the
    // image through the extra rotation transform and the drawings directly.
    //
    // Transforms compose innermost-last, so a stored point travels
    // rotate → crop-shift → downscale, which is the spec's fixed render order:
    // rotate → crop → drawings. The timestamp is painted after `restore()`, in
    // output space, so it is never rotated.
    ctx.save();
    ctx.scale(dstW / srcW, dstH / srcH);
    ctx.translate(-srcX, -srcY);

    ctx.save();
    applyRotationTransform(ctx, rotation, img.naturalWidth, img.naturalHeight);
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
    ctx.restore();

    renderAllDrawings(ctx, drawings, null);
    ctx.restore();

    drawTimestamp(ctx, dstW, dstH, resolveTimestamp(input.file));

    return canvas;
}

export function resolveTimestamp(file: File): Date {
    const lm = file.lastModified;
    const now = Date.now();
    if (lm && lm > 0 && lm < now + 60_000) return new Date(lm);
    return new Date(now);
}

// Day-month-year + 12h time, matched to the custom-date-range filter chip's
// dd/MM/yyyy ordering on the records page so the two surfaces read consistently.
const TIMESTAMP_PATTERN = "d MMM yyyy hh:mm a";

export function formatTimestamp(date: Date): string {
    return format(date, TIMESTAMP_PATTERN);
}

// Visual design mirrors otus-web's `stampDateOnImage` in
// components/features/image-viewer-modal/use-image-upload.ts — semi-transparent
// black box flush to the bottom-left, white sans-serif text, no shadow.
export function drawTimestamp(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    date: Date,
) {
    const baseSide = Math.min(width, height);
    const fontSize = Math.max(16, Math.round(baseSide * 0.035));
    const padding = Math.round(fontSize * 0.6);
    const text = formatTimestamp(date);

    ctx.save();
    ctx.font = `${fontSize}px sans-serif`;
    const textWidth = ctx.measureText(text).width;
    const textHeight = fontSize;

    const bgX = 0;
    const bgY = height - textHeight - padding * 2;
    const bgW = textWidth + padding * 2;
    const bgH = textHeight + padding * 2;

    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(bgX, bgY, bgW, bgH);

    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "top";
    ctx.fillText(text, padding, bgY + padding);
    ctx.restore();
}

function loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("renderAnnotatedBlob: image failed to load"));
        };
        img.src = url;
    });
}
